import type { MetricResult } from '@/domain/metrics/contracts';
import type { MetricsEngineResult } from '../selectors';

export interface ScenarioComparisonRow {
  metric: 'investment' | 'leads' | 'projectedClients' | 'projectedRevenue' | 'cpl' | 'cac' | 'roas' | 'roi';
  value: number | null;
  status: MetricResult['status'];
  /** null when the base value is unknown/zero (plan §14 — never divide by zero). */
  deltaAbsolute: number | null;
  deltaRatio: number | null;
}

export interface ScenarioComparisonColumn {
  /** undefined for Base. */
  scenarioId?: string;
  scenarioName: string;
  rows: ScenarioComparisonRow[];
  warnings: MetricsEngineResult['funnel']['warnings'];
}

const METRICS: ScenarioComparisonRow['metric'][] = [
  'investment',
  'leads',
  'projectedClients',
  'projectedRevenue',
  'cpl',
  'cac',
  'roas',
  'roi',
];

function metricResult(funnel: MetricsEngineResult['funnel'], metric: ScenarioComparisonRow['metric']): MetricResult {
  switch (metric) {
    case 'projectedRevenue':
      return funnel.revenue.status === 'known' ? funnel.revenue : funnel.projectedRevenue;
    default:
      return funnel[metric];
  }
}

/**
 * SPEC-009 §53-56/plan §13-14 — pure comparison table builder. A delta
 * ratio is only ever computed when the base value is known and non-zero
 * (AC-048); unknown stays unknown rather than being excluded from the
 * comparison (AC-049).
 */
export function compareScenarios(
  base: MetricsEngineResult,
  scenarios: readonly { scenarioId?: string; scenarioName: string; result: MetricsEngineResult }[],
): { base: ScenarioComparisonColumn; scenarios: ScenarioComparisonColumn[] } {
  const baseRows: ScenarioComparisonRow[] = METRICS.map((metric) => {
    const result = metricResult(base.funnel, metric);
    return {
      metric,
      value: result.status === 'known' ? (result.effectiveValue as number) : null,
      status: result.status,
      deltaAbsolute: null,
      deltaRatio: null,
    };
  });

  const scenarioColumns = scenarios.map(({ scenarioId, scenarioName, result }) => {
    const rows: ScenarioComparisonRow[] = METRICS.map((metric) => {
      const scenarioResult = metricResult(result.funnel, metric);
      const baseResult = metricResult(base.funnel, metric);
      const value = scenarioResult.status === 'known' ? (scenarioResult.effectiveValue as number) : null;
      const baseValue = baseResult.status === 'known' ? (baseResult.effectiveValue as number) : null;

      let deltaAbsolute: number | null = null;
      let deltaRatio: number | null = null;
      if (value !== null && baseValue !== null) {
        deltaAbsolute = value - baseValue;
        deltaRatio = baseValue !== 0 ? deltaAbsolute / baseValue : null;
      }

      return { metric, value, status: scenarioResult.status, deltaAbsolute, deltaRatio };
    });
    return { scenarioId, scenarioName, rows, warnings: result.funnel.warnings };
  });

  return {
    base: { scenarioName: 'Base', rows: baseRows, warnings: base.funnel.warnings },
    scenarios: scenarioColumns,
  };
}
